import { useState, useEffect, useCallback } from 'react';
import { auditApi } from '../services/api';
import { 
  AuditLogDto, 
  AuditLogFilters, 
  PaginationDto 
} from '../types/audit';
import { useAuth } from '../contexts/AuthContext';

export function useAuditLogs(initialFilters: AuditLogFilters = {}) {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLogDto[]>([]);
  const [pagination, setPagination] = useState<PaginationDto>({
    page: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    pageSize: 50,
    ...initialFilters,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await auditApi.getLogs(filters);
      setLogs(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setLogs([]);
      setPagination({
        page: 1,
        pageSize: 50,
        totalItems: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, user]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const updateFilters = useCallback((newFilters: Partial<AuditLogFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const refresh = useCallback(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    pagination,
    filters,
    loading,
    error,
    updateFilters,
    goToPage,
    refresh,
  };
}
